import React from 'react';
import { Poppins } from 'src/common';
import styled from 'styled-components';
import { normalizeHexColor } from 'src/utils/color';

const BrandColorsEditorDiv = styled.div`
  .logo-primary__editor {
    width: 360px;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.08);
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: absolute;
    top: 42px;
    left: 0;
    z-index: 20;
  }

  .logo-primary__editor-head {
    padding: 16px 18px 12px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  }

  .logo-primary__editor-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 18px;
  }

  .logo-primary__section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .logo-primary__input-row {
    display: flex;
    align-items: center;
    gap: 12px;
    background-color: ${({ theme }) => theme.colors.cultured};
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    border-radius: 8px;
    padding: 10px 12px;
  }

  .logo-primary__input-label {
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.cflowerBlue};
    letter-spacing: 0.2px;
  }

  .logo-primary__input {
    flex: 1;
    border: none;
    font-size: 14px;
    font-family: Poppins, sans-serif;
    color: ${({ theme }) => theme.colors.prussianBlue};
    background-color: transparent;
    outline: none;
    text-align: right;
  }

  .logo-primary__input-swatch {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    background-color: transparent;
  }

  .logo-primary__input-swatch--empty {
    background-color: ${({ theme }) => theme.colors.white};
    background-image: linear-gradient(
        45deg,
        ${({ theme }) => theme.colors.stroke} 25%,
        transparent 25%,
        transparent 75%,
        ${({ theme }) => theme.colors.stroke} 75%,
        ${({ theme }) => theme.colors.stroke}
      ),
      linear-gradient(
        45deg,
        ${({ theme }) => theme.colors.stroke} 25%,
        transparent 25%,
        transparent 75%,
        ${({ theme }) => theme.colors.stroke} 75%,
        ${({ theme }) => theme.colors.stroke}
      );
    background-size: 6px 6px;
    background-position:
      0 0,
      3px 3px;
    background-repeat: repeat;
    background-origin: border-box;
    background-clip: border-box;
  }

  .logo-primary__actions {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 0 18px 18px;
    justify-content: space-between;
    width: 100%;
  }

  .logo-primary__action {
    border-radius: 6px;
    padding: 8px 16px;
    min-width: 92px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    font-family: Poppins, sans-serif;
    cursor: pointer;
    border: 1px solid transparent;
  }

  .logo-primary__action--ghost {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.error};
    border-color: ${({ theme }) => theme.colors.stroke};
  }

  .logo-primary__action--primary {
    background: ${({ theme }) => theme.colors.gradient};
    color: ${({ theme }) => theme.colors.white};
  }

  .logo-primary__action--reset {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.error};
    border-color: ${({ theme }) => theme.colors.stroke};
  }

  .logo-primary__action--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

interface BrandColorsEditorProps {
  primaryColorDraft: string;
  secondaryColorDraft: string;
  primaryColorError: string | null;
  secondaryColorError: string | null;
  currentPrimaryColor: string | null;
  currentSecondaryColor: string | null;
  isUpdatingColors: boolean;
  canEditBranding: boolean;
  canUpdateBrandColors: boolean;
  onPrimaryChange: (value: string) => void;
  onSecondaryChange: (value: string) => void;
  onClose: () => void;
  onReset: () => void;
  onUpdate: () => void;
}

export const BrandColorsEditor: React.FC<BrandColorsEditorProps> = ({
  primaryColorDraft,
  secondaryColorDraft,
  primaryColorError,
  secondaryColorError,
  currentPrimaryColor,
  currentSecondaryColor,
  isUpdatingColors,
  canEditBranding,
  canUpdateBrandColors,
  onPrimaryChange,
  onSecondaryChange,
  onClose,
  onReset,
  onUpdate,
}) => {
  return (
    <BrandColorsEditorDiv>
      <div className="logo-primary__editor">
        <div className="logo-primary__editor-head">
          <Poppins px={16} weight={600} color="prussianBlue">
            Brand colours
          </Poppins>
        </div>
        <div className="logo-primary__editor-body">
          <div className="logo-primary__section">
            <Poppins px={12} weight={600} color="cflowerBlue">
              Primary Colour
            </Poppins>
            <div className="logo-primary__input-row">
              <span className="logo-primary__input-label">HEX Code:</span>
              <input
                className="logo-primary__input"
                value={primaryColorDraft}
                onChange={(event) => onPrimaryChange(event.target.value)}
                placeholder="#1A2B3C"
                maxLength={7}
                disabled={!canEditBranding || isUpdatingColors}
              />
              <span
                className={[
                  'logo-primary__input-swatch',
                  !(normalizeHexColor(primaryColorDraft) || currentPrimaryColor) &&
                    'logo-primary__input-swatch--empty',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  backgroundColor: normalizeHexColor(primaryColorDraft) || currentPrimaryColor || undefined,
                }}
              />
            </div>
            {primaryColorError && (
              <Poppins px={12} color="error">
                {primaryColorError}
              </Poppins>
            )}
          </div>
          <div className="logo-primary__section">
            <Poppins px={12} weight={600} color="cflowerBlue">
              Secondary Colour
            </Poppins>
            <div className="logo-primary__input-row">
              <span className="logo-primary__input-label">HEX Code:</span>
              <input
                className="logo-primary__input"
                value={secondaryColorDraft}
                onChange={(event) => onSecondaryChange(event.target.value)}
                placeholder="#1A2B3C"
                maxLength={7}
                disabled={!canEditBranding || isUpdatingColors}
              />
              <span
                className={[
                  'logo-primary__input-swatch',
                  !(normalizeHexColor(secondaryColorDraft) || currentSecondaryColor) &&
                    'logo-primary__input-swatch--empty',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  backgroundColor:
                    normalizeHexColor(secondaryColorDraft) || currentSecondaryColor || undefined,
                }}
              />
            </div>
            {secondaryColorError && (
              <Poppins px={12} color="error">
                {secondaryColorError}
              </Poppins>
            )}
          </div>
        </div>
        <div className="logo-primary__actions">
          <button
            type="button"
            className="logo-primary__action logo-primary__action--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={[
              'logo-primary__action',
              'logo-primary__action--reset',
              isUpdatingColors && 'logo-primary__action--disabled',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onReset}
            disabled={isUpdatingColors}
          >
            Reset
          </button>
          <button
            type="button"
            className={[
              'logo-primary__action',
              'logo-primary__action--primary',
              !canUpdateBrandColors && 'logo-primary__action--disabled',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onUpdate}
            disabled={!canUpdateBrandColors}
          >
            Update
          </button>
        </div>
      </div>
    </BrandColorsEditorDiv>
  );
};
